import { AxiosInstance } from 'axios';

const PUBLIC_AUTH_PATHS = [
    '/auth/login',
    '/auth/set-new-password',
    '/auth/register-company',
    '/auth/companies/search'
];

/** Public authentication failures must stay on the form so it can show details. */
export const isPublicAuthRequest = (url: string): boolean => (
    PUBLIC_AUTH_PATHS.some((path) => url.includes(path))
);

/**
 * Centralized handling of authentication failures (HTTP 401).
 *
 * Several services create their own `axios.create()` instances, which do NOT
 * inherit the global response interceptor registered in auth.service.optimized.ts.
 * Without this, a 401 on those instances surfaces as a confusing feature-specific
 * error like "Failed to load company information" instead of logging the user out.
 *
 * Attaching this interceptor to every custom axios instance ensures any 401 is
 * handled consistently: clear auth state and redirect to the login page, while
 * preserving the reason when another browser has replaced the active session.
 */
export const handleAuthError = (error: any): void => {
    const status = error?.response?.status;
    if (status !== 401) {
        return;
    }

    const url: string = error?.config?.url || '';
    if (isPublicAuthRequest(url)) {
        return;
    }

    // The language endpoint can 401 transiently right after login (Edge timing
    // issue); don't force a logout for it.
    if (url.includes('/system-settings/language')) {
        return;
    }

    if (error?.response?.data?.code === 'SESSION_REVOKED') {
        try {
            sessionStorage.setItem('logoutReason', 'SESSION_REVOKED');
        } catch {
            // The user is still logged out if session storage is unavailable.
        }
    }

    // Clear auth state. A full-page redirect below wipes any in-memory caches.
    try {
        localStorage.removeItem('token');
        localStorage.removeItem('userInfo');
    } catch (e) {
        // Ignore storage failures.
    }

    // Avoid redirect loops if we're already on the login page.
    if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
    }
};

/**
 * Attach the shared 401 handler to a custom axios instance.
 */
export const attachAuthResponseInterceptor = (instance: AxiosInstance): void => {
    instance.interceptors.response.use(
        (response) => response,
        (error) => {
            handleAuthError(error);
            return Promise.reject(error);
        }
    );
};
